import { css } from 'lit';

/*
  Design primitives — the reusable building blocks of the navy/gold
  restyle (north star: the PagAsenso Flutter scanner). Include this in a
  component's `static styles` array alongside the page's own styles and
  the classes below become available inside that shadow root.

  These are driven entirely by the --s-* semantic token layer defined in
  global.css (:root for light, .sl-theme-dark for dark). Custom
  properties inherit through the shadow boundary, so every primitive
  flips light/dark automatically without a :host-context() selector —
  define the look once here, theme it once in global.css.

  Primitives:
    .page-head            large display title + muted subtitle
    .icon-tile            gold glyph on a tinted-navy rounded square
    .list-row             icon-tile · title · subtitle · chevron row
    .btn-primary-fill     solid-navy full-width tall CTA, white text
    .btn-secondary-pill   outlined pill, leading icon, navy text
    .field-tall           sl-input/sl-select sized + leading-icon styled

  Gold is an accent only (glyphs, the brand mark, active markers) — never
  a fill on a large neutral surface. The one allowed large gold surface
  is the single primary CTA in dark mode, where navy-on-navy would
  vanish (--s-primary flips to gold in .sl-theme-dark).
*/
export const designPrimitives = css`
  /* ============================================== */
  /* Page head                                      */
  /* ============================================== */
  .page-head {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 4px 0 22px;
  }
  .page-head__title {
    font-family: var(--font-display, inherit);
    font-weight: 800;
    font-size: var(--s-type-display, 2rem);
    line-height: 1.05;
    letter-spacing: -0.02em;
    color: var(--s-ink, currentColor);
    margin: 0;
  }
  .page-head__sub {
    font-size: var(--s-type-body, 0.95rem);
    color: var(--s-ink-soft, #5b6781);
    max-width: 62ch;
    line-height: 1.45;
    margin: 0;
  }

  /* ============================================== */
  /* Icon tile — gold glyph on tinted-navy square   */
  /* ============================================== */
  .icon-tile {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: var(--s-canvas-strong, rgba(15, 30, 55, 0.06));
    color: var(--s-accent, #f2b441);
    font-size: 26px;
  }
  .icon-tile.sm {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    font-size: 21px;
  }

  /* ============================================== */
  /* List row — icon · title · subtitle · chevron   */
  /* ============================================== */
  .list-row {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    box-sizing: border-box;
    padding: 14px 16px;
    background: var(--s-surface, #fff);
    border: 1px solid var(--s-line, rgba(15, 30, 55, 0.08));
    border-radius: 16px;
    box-shadow: var(--app-shadow-sm, 0 1px 2px rgba(15, 30, 55, 0.05));
    color: inherit;
    text-align: left;
    text-decoration: none;
    font: inherit;
    cursor: pointer;
    transition:
      transform 140ms ease,
      box-shadow 140ms ease,
      border-color 140ms ease;
  }
  .list-row:hover {
    transform: translateY(-1px);
    box-shadow: var(--app-shadow-md, 0 6px 18px rgba(15, 30, 55, 0.08));
  }
  .list-row:focus-visible {
    outline: none;
    border-color: var(--s-accent, #f2b441);
    box-shadow: var(--sl-focus-ring, 0 0 0 3px rgba(15, 30, 55, 0.25));
  }
  .list-row[disabled],
  .list-row.is-static {
    cursor: default;
  }
  .list-row[disabled]:hover,
  .list-row.is-static:hover {
    transform: none;
    box-shadow: var(--app-shadow-sm, 0 1px 2px rgba(15, 30, 55, 0.05));
  }
  .list-row__body {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .list-row__title {
    font-family: var(--font-display, inherit);
    font-weight: 700;
    font-size: 1.02rem;
    line-height: 1.25;
    color: var(--s-ink, currentColor);
    letter-spacing: -0.01em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .list-row__sub {
    font-size: var(--s-type-meta, 0.82rem);
    color: var(--s-ink-soft, #5b6781);
    line-height: 1.35;
  }
  .list-row__trail {
    flex: none;
    display: flex;
    align-items: center;
    gap: 10px;
    font-variant-numeric: tabular-nums;
  }
  .list-row__chevron {
    flex: none;
    color: var(--s-ink-soft, #5b6781);
    font-size: 20px;
    opacity: 0.7;
  }

  /* ============================================== */
  /* Primary fill button — solid navy, full width   */
  /* ============================================== */
  .btn-primary-fill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    box-sizing: border-box;
    min-height: 56px;
    padding: 0 22px;
    border: none;
    border-radius: 14px;
    background: var(--s-primary, #0f1e37);
    color: var(--s-primary-ink-on, #fff);
    font-family: var(--font-body, inherit);
    font-weight: 700;
    font-size: 1rem;
    letter-spacing: -0.005em;
    line-height: 1;
    cursor: pointer;
    box-shadow: var(--app-shadow-sm, 0 1px 2px rgba(15, 30, 55, 0.06));
    transition:
      transform 140ms ease,
      box-shadow 140ms ease,
      background 160ms ease;
  }
  .btn-primary-fill:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: var(--app-shadow-md, 0 6px 18px rgba(15, 30, 55, 0.1));
  }
  .btn-primary-fill:active:not(:disabled) {
    transform: none;
    background: var(--s-primary-press, #08122a);
  }
  .btn-primary-fill:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
  .btn-primary-fill sl-icon {
    font-size: 20px;
  }

  /* ============================================== */
  /* Secondary pill — outlined, leading icon         */
  /* ============================================== */
  .btn-secondary-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-sizing: border-box;
    min-height: 52px;
    padding: 0 22px;
    border-radius: 999px;
    /* Fallback solid border first for WebViews without color-mix()
       (older low-end Android), then the tinted version where supported. */
    border: 1.5px solid var(--s-line, rgba(15, 30, 55, 0.18));
    border-color: color-mix(in srgb, var(--s-ink, #0f1e37) 24%, transparent);
    background: transparent;
    color: var(--s-ink, currentColor);
    font-family: var(--font-body, inherit);
    font-weight: 700;
    font-size: 0.95rem;
    line-height: 1;
    cursor: pointer;
    transition:
      transform 140ms ease,
      border-color 140ms ease,
      background 160ms ease;
  }
  .btn-secondary-pill:hover:not(:disabled) {
    border-color: var(--s-ink-soft, #5b6781);
    border-color: color-mix(in srgb, var(--s-ink, #0f1e37) 45%, transparent);
    background: var(--s-canvas-strong, rgba(15, 30, 55, 0.05));
    background: color-mix(in srgb, var(--s-ink, #0f1e37) 5%, transparent);
  }
  .btn-secondary-pill:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .btn-secondary-pill sl-icon {
    font-size: 18px;
    color: var(--s-accent, #f2b441);
  }
  .btn-secondary-pill.block {
    width: 100%;
  }
  .btn-secondary-pill.sm {
    min-height: 38px;
    padding: 0 16px;
    font-size: 0.85rem;
    border-width: 1.5px;
  }
  .btn-secondary-pill.sm sl-icon {
    font-size: 16px;
  }
  .btn-secondary-pill.danger {
    color: var(--s-danger, #b3261e);
    border-color: var(--s-danger, #b3261e);
    border-color: color-mix(in srgb, var(--s-danger, #b3261e) 38%, transparent);
  }
  .btn-secondary-pill.danger sl-icon {
    color: var(--s-danger, #b3261e);
  }
  .btn-secondary-pill.danger:hover:not(:disabled) {
    border-color: var(--s-danger, #b3261e);
    border-color: color-mix(in srgb, var(--s-danger, #b3261e) 60%, transparent);
    background: var(--s-danger-soft, rgba(179, 38, 30, 0.08));
  }

  /* ============================================== */
  /* Tall field — sl-input / sl-select with prefix  */
  /* ============================================== */
  .field-tall {
    --sl-input-height-medium: 52px;
    --sl-input-border-radius-medium: 12px;
  }
  .field-tall::part(form-control-label) {
    font-weight: 600;
    color: var(--s-ink, currentColor);
    margin-bottom: 6px;
  }
  .field-tall::part(prefix) {
    color: var(--s-ink-soft, #5b6781);
    padding-left: 4px;
  }
  .field-tall sl-icon[slot='prefix'] {
    font-size: 19px;
  }
`;
